import type { EstadoTurno, TurnoReadDto } from '../services/turnoService';

export const GRID_HORA_INICIO = 8;
export const GRID_HORA_FIN = 20;
/** Resolución de 15 min para que turnos cortos ocupen una fila completa. */
export const GRID_SLOT_MINUTOS = 15;
export const GRID_ROW_PX = 34;

/** Altura mínima de bloque (turnos de 15 min legibles). */
export const MIN_TURNO_HEIGHT_PX = 26;

/** Altura visible del panel de horarios (scroll para el resto). */
export const GRID_VIEWPORT_MAX_PX = { xs: 340, md: 420 } as const;

export interface TurnoGridLayout {
  id: number | string;
  top: number;
  height: number;
  column: number;
  columns: number;
}

const turnoFinMin = (t: TurnoReadDto): number =>
  parseHoraMinutos(t.horaInicio) + Math.max(0, t.duracionMinutos);

/** Solapamiento real: comparten minutos (fin = inicio del otro no cuenta). */
const turnosSeSolapan = (a: TurnoReadDto, b: TurnoReadDto): boolean => {
  const a0 = parseHoraMinutos(a.horaInicio);
  const a1 = turnoFinMin(a);
  const b0 = parseHoraMinutos(b.horaInicio);
  const b1 = turnoFinMin(b);
  return a0 < b1 && b0 < a1;
};

const agruparTurnosSolapados = (turnos: TurnoReadDto[]): TurnoReadDto[][] => {
  const clusters: TurnoReadDto[][] = [];

  for (const t of turnos) {
    const indices: number[] = [];
    clusters.forEach((cluster, idx) => {
      if (cluster.some((c) => turnosSeSolapan(c, t))) indices.push(idx);
    });

    if (indices.length === 0) {
      clusters.push([t]);
      continue;
    }

    const merged = [t];
    for (let i = indices.length - 1; i >= 0; i -= 1) {
      merged.push(...clusters[indices[i]]);
      clusters.splice(indices[i], 1);
    }
    clusters.push(merged);
  }

  return clusters;
};

/** Reparte turnos solapados en columnas para que no se pisen. */
export const layoutTurnosGrilla = (turnos: TurnoReadDto[]): Map<string, TurnoGridLayout> => {
  const result = new Map<string, TurnoGridLayout>();
  if (turnos.length === 0) return result;

  for (const cluster of agruparTurnosSolapados(turnos)) {
    const sorted = [...cluster].sort(
      (a, b) =>
        parseHoraMinutos(a.horaInicio) - parseHoraMinutos(b.horaInicio) ||
        turnoFinMin(b) - turnoFinMin(a),
    );

    const finPorColumna: number[] = [];
    const columnaPorId = new Map<string, number>();

    for (const t of sorted) {
      const inicio = parseHoraMinutos(t.horaInicio);
      let col = finPorColumna.findIndex((fin) => fin <= inicio);
      if (col === -1) {
        col = finPorColumna.length;
        finPorColumna.push(turnoFinMin(t));
      } else {
        finPorColumna[col] = turnoFinMin(t);
      }
      columnaPorId.set(String(t.id), col);
    }

    const totalColumns = Math.max(1, finPorColumna.length);

    for (const t of sorted) {
      const col = columnaPorId.get(String(t.id)) ?? 0;
      result.set(String(t.id), {
        id: t.id,
        top: turnoGridTopPx(t.horaInicio),
        height: turnoGridHeightPx(t.duracionMinutos),
        column: col,
        columns: totalColumns,
      });
    }
  }

  return result;
};

export const shouldShowSlotLabel = (minutosDesdeMedianoche: number): boolean =>
  minutosDesdeMedianoche % 30 === 0 || minutosDesdeMedianoche === GRID_HORA_FIN * 60;

export const parseHoraMinutos = (hora: string): number => {
  const match = /^(\d{1,2}):(\d{2})/.exec(String(hora).trim());
  if (!match) return 0;
  return Number(match[1]) * 60 + Number(match[2]);
};

export const minutosAHora = (total: number): string => {
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const formatHora12 = (hora: string): string => {
  const mins = parseHoraMinutos(hora);
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

export const horaFinTurno = (horaInicio: string, duracionMinutos: number): string =>
  minutosAHora(parseHoraMinutos(horaInicio) + Math.max(0, duracionMinutos));

export const formatRangoHorario = (horaInicio: string, duracionMinutos: number): string =>
  `${formatHora12(horaInicio)} - ${formatHora12(horaFinTurno(horaInicio, duracionMinutos))}`;

export const toIsoDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const parseIsoDate = (iso: string): Date => {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
};

export const COLOR_ESTADO_TURNO: Record<EstadoTurno, string> = {
  PROGRAMADO: '#90caf9',
  RECIBIDO: '#4fc3f7',
  EN_TRABAJO: '#fff176',
  FACTURADO: '#a5d6a7',
};

export const BORDER_ESTADO_TURNO: Record<EstadoTurno, string> = {
  PROGRAMADO: '#1976d2',
  RECIBIDO: '#0288d1',
  EN_TRABAJO: '#f9a825',
  FACTURADO: '#388e3c',
};

export const gridSlots = (): number[] => {
  const slots: number[] = [];
  const fin = GRID_HORA_FIN * 60;
  for (let m = GRID_HORA_INICIO * 60; m <= fin; m += GRID_SLOT_MINUTOS) {
    slots.push(m);
  }
  return slots;
};

export const labelSlotHora = (minutosDesdeMedianoche: number): string => {
  const h = Math.floor(minutosDesdeMedianoche / 60);
  const m = minutosDesdeMedianoche % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export const turnoGridTopPx = (horaInicio: string): number => {
  const start = parseHoraMinutos(horaInicio);
  const gridStart = GRID_HORA_INICIO * 60;
  const offsetMin = Math.max(0, start - gridStart);
  return (offsetMin / GRID_SLOT_MINUTOS) * GRID_ROW_PX;
};

export const turnoGridHeightPx = (duracionMinutos: number): number =>
  Math.max(MIN_TURNO_HEIGHT_PX, (duracionMinutos / GRID_SLOT_MINUTOS) * GRID_ROW_PX);
